// TLM3D standalone structures: antennas, towers, walls, power lines, dams,
// avalanche barriers, fountains, monuments... (bake artifact objects.json,
// APP_OBJECTS.md). Decor pass: MeshBasicMaterial like everything else —
// shading is faked per-face at build time, zero runtime lights, and the whole
// layer merges into TWO draw calls (solid mesh + cable lines; the cable
// lines are wires of their real diameter, wire.js).
// The WALKABLE archetypes (jetty decks + the ring-topped berm/basin/dam)
// also register building-collider entries (colEntries below): walk on
// top, and from below the deck is a ceiling. Everything else (masts, walls,
// avalanche rows...) stays walk-through decor — same rationale as station
// furniture: a collider on every thin steel row risks trapping bodies.
//
// Color: `c` is the SWISSIMAGE ortho pixel the bake sampled at the object —
// the data-true color. It is USED where the ortho actually images the
// structure (walls, dams, silos, ramps: seen from above at 2 m they are their
// own pixels) and IGNORED for thin vertical steel (a mast is sub-pixel; its
// ortho color is the meadow it stands on) — those get declared archetype
// colors instead.
import * as THREE from 'three';
import { fetchAsset } from './assetq.js';
import { runSteps } from './decode.js';
import { wireDiameter, fenceWireD, wireMaterial, wireBeforeRender, WIRE_ORDER } from './wire.js';

// archetype base colors (used when the ortho pixel is not the structure)
const COL = {
  mast: [0.88, 0.88, 0.86], tower: [0.72, 0.7, 0.66], chimney: [0.55, 0.5, 0.48],
  windturbine: [0.92, 0.93, 0.94], silo: [0.78, 0.77, 0.72], wall: [0.6, 0.58, 0.54],
  dam: [0.66, 0.65, 0.62], avalanche: [0.45, 0.42, 0.38], powerline: [0.5, 0.51, 0.54],
  fountain: [0.6, 0.6, 0.58], monument: [0.7, 0.68, 0.62], skijump: [0.85, 0.86, 0.88],
  utility: [0.74, 0.73, 0.7], waterfall: [0.88, 0.92, 0.96], jetty: [0.5, 0.42, 0.33],
  chute: [0.87, 0.9, 0.93], lane: [0.66, 0.42, 0.38], court: [0.55, 0.62, 0.5],
  checkdam: [0.52, 0.52, 0.49], coping: [0.64, 0.63, 0.6],
  platform: [0.62, 0.61, 0.58], basin: [0.6, 0.62, 0.6], berm: [0.5, 0.52, 0.42],
  generic: [0.7, 0.7, 0.7],
};
// archetypes whose ortho pixel IS the structure seen from above
const ORTHO_COLORED = new Set(['wall', 'dam', 'silo', 'skijump', 'fountain',
  'avalanche', 'court', 'lane', 'platform', 'basin', 'berm', 'jetty']);

// the cable lines' sink: xyz pairs + per vertex the wire (unit tangent, diameter m)
class Cables {
  constructor() { this.pos = []; this.wire = []; }
  seg(x0, y0, z0, x1, y1, z1, d) {
    const l = Math.hypot(x1 - x0, y1 - y0, z1 - z0) || 1, tx = (x1 - x0) / l, ty = (y1 - y0) / l, tz = (z1 - z0) / l;
    this.pos.push(x0, y0, z0, x1, y1, z1);
    this.wire.push(tx, ty, tz, d, tx, ty, tz, d);
  }
}

class Acc {
  constructor() { this.pos = []; this.col = []; this.idx = []; }

  // fake-lambert per face: brighter tops, sides keyed off the normal
  shade(nx, ny, nz, c) {
    const s = 0.68 + 0.3 * Math.max(0, ny) + 0.09 * nx + 0.05 * nz;
    return [c[0] * s, c[1] * s, c[2] * s];
  }

  quad(a, b, c2, d, col) {
    const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const v = [d[0] - a[0], d[1] - a[1], d[2] - a[2]];
    let nx = u[1] * v[2] - u[2] * v[1], ny = u[2] * v[0] - u[0] * v[2],
      nz = u[0] * v[1] - u[1] * v[0];
    const l = Math.hypot(nx, ny, nz) || 1;
    const cc = this.shade(nx / l, ny / l, nz / l, col);
    const base = this.pos.length / 3;
    for (const p of [a, b, c2, d]) { this.pos.push(p[0], p[1], p[2]); this.col.push(cc[0], cc[1], cc[2]); }
    this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  // axis-aligned-ish box centred at (x, y+sy/2, z), rotated yaw around Y
  box(x, y, z, sx, sy, sz, col, yaw = 0) {
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const R = (dx, dz) => [x + dx * c - dz * s, z + dx * s + dz * c];
    const hx = sx / 2, hz = sz / 2;
    const p = [];
    for (const [dx, dz, dy] of [[-hx, -hz, 0], [hx, -hz, 0], [hx, hz, 0], [-hx, hz, 0],
      [-hx, -hz, 1], [hx, -hz, 1], [hx, hz, 1], [-hx, hz, 1]]) {
      const [px, pz] = R(dx, dz);
      p.push([px, y + dy * sy, pz]);
    }
    this.quad(p[4], p[5], p[6], p[7], col);                    // top
    this.quad(p[0], p[1], p[5], p[4], col);
    this.quad(p[1], p[2], p[6], p[5], col);
    this.quad(p[2], p[3], p[7], p[6], col);
    this.quad(p[3], p[0], p[4], p[7], col);
  }

  // vertical tapered tube (n segments), r0 at base, r1 at top
  tube(x, y, z, r0, r1, h, col, n = 10, capTop = true) {
    const ring = (r, yy) => {
      const out = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        out.push([x + Math.cos(a) * r, yy, z + Math.sin(a) * r]);
      }
      return out;
    };
    const b = ring(r0, y), t = ring(r1, y + h);
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      this.quad(b[i], b[j], t[j], t[i], col);
    }
    if (capTop) {
      const base = this.pos.length / 3;
      const cc = this.shade(0, 1, 0, col);
      this.pos.push(x, y + h, z); this.col.push(cc[0], cc[1], cc[2]);
      for (const p of t) { this.pos.push(p[0], p[1], p[2]); this.col.push(cc[0], cc[1], cc[2]); }
      for (let i = 0; i < n; i++) this.idx.push(base, base + 1 + i, base + 1 + ((i + 1) % n));
    }
  }
}

const oc = (o, arch) => {
  if (o.c && ORTHO_COLORED.has(arch)) return [o.c[0] / 255, o.c[1] / 255, o.c[2] / 255];
  return COL[arch] || COL.generic;
};

// ------------------------------------------------------------- archetypes

// The transmitter mast — every `mast` record (TLM 'Antenne klein' / 'gross'
// / 'Sendeturm'; owner 2026-10-20): a tapered square lattice in alternating
// red / white aviation bands (odd count, red at both ends) ONLY above
// MAST_BAND_MIN_H (owner ruling "only masts over 60m"); a mast <= that draws
// the same lattice + head in ONE plain galvanised grey, no bands. The antenna
// head at the shaft top (3 sector panels, one dish) and a whip carrying the
// obstruction light (red when banded). Everything ends AT o.y + o.h, the
// surveyed tip. Colour only: tris and geometry are identical either way.
// Tris: 48 per band (4 leg beams, 4 face diagonals, 4 band-top strips) +
// MAST_HEAD_TRIS (3 panel boxes, the dish fan, the whip beam, the light box):
// <= 48 * MAST_BANDS_MAX + MAST_HEAD_TRIS = 392 <= validate OBJ_TRI mast
// (check-objects pins it). No collider: walk-through decor, as before.
export const MAST_BANDS_MAX = 7;
export const MAST_HEAD_TRIS = 30 + 8 + 8 + 10;
export const MAST_BAND_MIN_H = 60;                 // m: aviation bands only above
const MAST_GREY = [0.5, 0.52, 0.54];               // galvanised steel, plain
const MAST_RED = [0.6, 0.05, 0.035], MAST_WHITE = [0.88, 0.88, 0.86];
const MAST_PANEL = [0.8, 0.8, 0.78], MAST_STEEL = [0.45, 0.46, 0.48];
export function mastBands(hs) {
  return Math.min(MAST_BANDS_MAX, Math.max(3, 2 * Math.round((hs / 7 - 1) / 2) + 1));
}
function mast(A, o) {
  const { x, y, z, h } = o;
  const whip = Math.min(6, Math.max(1, h * 0.1)), hs = h - whip;  // shaft top
  const w0 = Math.min(4, Math.max(1.2, h * 0.07)), w1 = Math.max(0.6, w0 * 0.4);
  const nb = mastBands(hs);
  const banded = h > MAST_BAND_MIN_H;
  const cRed = banded ? MAST_RED : MAST_GREY, cWhite = banded ? MAST_WHITE : MAST_GREY;
  const cPanel = banded ? MAST_PANEL : MAST_GREY, cSteel = banded ? MAST_STEEL : MAST_GREY;
  const C = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  const corner = (t, k) => {
    const r = (w0 + (w1 - w0) * t) / 2;
    return [x + C[k][0] * r, y + hs * t, z + C[k][1] * r];
  };
  // a flat strip p -> q, dy tall (the pylon braces' pattern; DoubleSide)
  const strip = (p, q, dy, col) => A.quad(p, [p[0], p[1] + dy, p[2]], [q[0], q[1] + dy, q[2]], q, col);
  for (let i = 0; i < nb; i++) {
    const t0 = i / nb, t1 = (i + 1) / nb, col = i % 2 ? cWhite : cRed;
    for (let k = 0; k < 4; k++) {
      const k1 = (k + 1) % 4;
      beam(A, corner(t0, k), corner(t1, k), 0.3, col);
      // zigzag: the diagonal flips side every band
      strip(corner(t0, i % 2 ? k : k1), corner(t1, i % 2 ? k1 : k), 0.2, col);
      strip(corner(t1, k), corner(t1, k1), -0.25, col);
    }
  }
  // the head: 3 sector panels on the top band (120 deg apart), a dish below
  const L = Math.min(2.6, Math.max(1.2, h * 0.07)), s = Math.min(1, h / 20);
  const rp = w1 / 2 + 0.3;
  for (let k = 0; k < 3; k++) {
    const a = k * Math.PI * 2 / 3;
    A.box(x + Math.cos(a) * rp, y + hs - L, z + Math.sin(a) * rp, 0.18, L, 0.4 * s + 0.15, cPanel, a);
  }
  const rd = 0.6 * s + 0.25, yd = y + Math.max(hs * 0.5, hs - L - rd - 0.6);
  const td = ((w0 + (w1 - w0) * (yd - y) / hs) / 2) + 0.35;   // in front of the +x face
  const D = [];
  for (let k = 0; k < 8; k++) {
    const a = k * Math.PI / 4;
    D.push([x + td, yd + Math.cos(a) * rd, z + Math.sin(a) * rd]);
  }
  const cen = [x + td + rd * 0.35, yd, z];
  for (let k = 0; k < 8; k += 2) A.quad(cen, D[k], D[k + 1], D[(k + 2) % 8], cPanel);
  beam(A, [x, y + hs, z], [x, y + h - 0.4, z], 0.12, cSteel);   // whip
  A.box(x, y + h - 0.4, z, 0.4, 0.4, 0.4, cRed);                   // obstruction light
}

function tower(A, o) {
  const c = o.c ? [0.55 + 0.2 * (o.c[0] / 255), 0.53 + 0.2 * (o.c[1] / 255),
    0.5 + 0.2 * (o.c[2] / 255)] : COL.tower;
  const r = Math.max(1.8, o.h * 0.14);
  A.tube(o.x, o.y, o.z, r, r * 0.85, o.h, c, 10);
  A.tube(o.x, o.y + o.h, o.z, r * 0.95, 0.3, r * 0.9, [0.42, 0.36, 0.33], 10); // roof cone
}

function chimney(A, o) {
  const r = Math.max(1.0, o.h * 0.05);
  A.tube(o.x, o.y, o.z, r, r * 0.6, o.h, COL.chimney, 10);
}

function windturbine(A, o) {
  const c = COL.windturbine, hub = o.h * 0.75;
  A.tube(o.x, o.y, o.z, Math.max(1.2, o.h * 0.03), 0.6, hub, c, 8);
  A.box(o.x, o.y + hub, o.z, 3.2, 2.2, 2.2, c);
  const bl = o.h * 0.32;
  for (let i = 0; i < 3; i++) {
    const a = i * (Math.PI * 2 / 3) + 0.35;
    // blade = thin box, rotated in the rotor plane (facing +x)
    const cy = o.y + hub + 1.1 + Math.sin(a) * bl / 2;
    A.box(o.x + 1.6, cy, o.z + Math.cos(a) * bl / 2, 0.25, Math.abs(Math.sin(a)) * bl + 0.8,
      Math.abs(Math.cos(a)) * bl + 0.8, c);
  }
}

function silo(A, o) {
  const c = oc(o, 'silo'), r = Math.max(2.4, o.h * 0.22);
  A.tube(o.x, o.y, o.z, r, r, o.h * 0.85, c, 12);
  A.tube(o.x, o.y + o.h * 0.85, o.z, r, r * 0.25, o.h * 0.15, c, 12);
}

// the [bottom, top] of a run point: [x, y, z] stands o.h on its ground y;
// [x, y, z, top] (a coping, bake.py coping_line) has its top on the
// smoothed high-side line and reaches down to the ground (at least o.h tall)
export function wallSpan(p, h) {
  return p.length > 3 ? [Math.min(p[1], p[3] - h), p[3]] : [p[1], p[1] + h];
}

function wallRuns(A, o, thick, capW) {
  const c = oc(o, o.a);
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, , z0] = run[i], [x1, , z1] = run[i + 1];
      const [y0, t0] = wallSpan(run[i], o.h), [y1, t1] = wallSpan(run[i + 1], o.h);
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz);
      if (L < 0.2) continue;
      const px = -dz / L * thick / 2, pz = dx / L * thick / 2;
      // two faces + top cap = a wall with visible thickness
      A.quad([x0 + px, y0, z0 + pz], [x1 + px, y1, z1 + pz],
        [x1 + px, t1, z1 + pz], [x0 + px, t0, z0 + pz], c);
      A.quad([x1 - px, y1, z1 - pz], [x0 - px, y0, z0 - pz],
        [x0 - px, t0, z0 - pz], [x1 - px, t1, z1 - pz], c);
      const cw = capW / thick;
      A.quad([x0 + px * cw, t0, z0 + pz * cw], [x1 + px * cw, t1, z1 + pz * cw],
        [x1 - px * cw, t1, z1 - pz * cw], [x0 - px * cw, t0, z0 - pz * cw], c);
    }
  }
}

function avalanche(A, o) {
  // Rows of steel snow bridges (Schutzverbauung — hundreds per alpine world).
  // One continuous BAND per run (the grating, upper half of the structure) +
  // sparse posts: reads as the real rows from any gameplay distance at ~1/15
  // the cost of modeling every bridge. DoubleSide material = 2 tris/segment.
  const c = COL.avalanche;
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, y0, z0] = run[i], [x1, y1, z1] = run[i + 1];
      A.quad([x0, y0 + o.h * 0.45, z0], [x1, y1 + o.h * 0.45, z1],
        [x1, y1 + o.h, z1], [x0, y0 + o.h, z0], c);
      const L = Math.hypot(x1 - x0, z1 - z0);
      for (let s = 0; s < L; s += 6.0) {
        const t = s / L;
        A.box(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, z0 + (z1 - z0) * t,
          0.22, o.h, 0.22, c);
      }
    }
  }
}

function powerline(A, cables, o) {
  const c = COL.powerline;
  for (const run of o.runs || []) {
    for (let i = 0; i < run.length; i++) {
      const [x, y, z] = run[i];
      A.box(x, y, z, 0.6, o.h, 0.6, c);                       // pylon shaft
      A.box(x, y + o.h - 1.6, z, 7.0, 0.35, 0.35, c,          // crossarm, span-aligned
        i + 1 < run.length ? Math.atan2(run[i + 1][2] - z, run[i + 1][0] - x) + Math.PI / 2
          : 0);
      if (i + 1 < run.length) {
        const [x1, y1, z1] = run[i + 1];
        const top0 = y + o.h - 1.6, top1 = y1 + o.h - 1.6;
        const span = Math.hypot(x1 - x, z1 - z);
        const sag = Math.min(5, span * 0.045);
        const N = 8;
        for (let k = 0; k < N; k++) {
          const t0 = k / N, t1 = (k + 1) / N;
          const cy = (t) => top0 + (top1 - top0) * t - sag * 4 * t * (1 - t);
          cables.seg(x + (x1 - x) * t0, cy(t0), z + (z1 - z) * t0,
            x + (x1 - x) * t1, cy(t1), z + (z1 - z) * t1, wireDiameter(o.kv, 0));
        }
      }
    }
  }
}

function fountain(A, o) {
  const c = oc(o, 'fountain');
  A.box(o.x, o.y, o.z, 2.6, Math.max(0.6, o.h), 1.4, c);
  A.box(o.x, o.y + Math.max(0.6, o.h), o.z, 2.2, 0.06, 1.0, [0.32, 0.44, 0.52]); // water
  A.box(o.x - 0.9, o.y, o.z, 0.18, Math.max(0.6, o.h) + 0.9, 0.18, c);           // spout
}

function monument(A, o) {
  const c = COL.monument;
  A.box(o.x, o.y, o.z, 1.6, 0.5, 1.6, c);
  A.box(o.x, o.y + 0.5, o.z, 0.7, o.h - 0.5, 0.7, c);
}

function skijump(A, o) {
  // inrun ribbon: elevated at the uphill end, on the ground at the downhill
  // end — reads as the tower+track silhouette from any gameplay distance
  const c = oc(o, 'skijump');
  const runs = o.runs || [];
  for (const run of runs) {
    if (run.length < 2) continue;
    const lift = Math.max(6, o.h * 3);
    for (let i = 0; i + 1 < run.length; i++) {
      const t0 = i / (run.length - 1), t1 = (i + 1) / (run.length - 1);
      const [x0, y0, z0] = run[i], [x1, y1, z1] = run[i + 1];
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz) || 1;
      const px = -dz / L * 1.8, pz = dx / L * 1.8;
      const l0 = y0 + lift * (1 - t0), l1 = y1 + lift * (1 - t1);
      A.quad([x0 + px, l0, z0 + pz], [x1 + px, l1, z1 + pz],
        [x1 - px, l1, z1 - pz], [x0 - px, l0, z0 - pz], c);
    }
    const [sx, sy, sz] = run[0];
    A.box(sx, sy, sz, 2.2, Math.max(6, o.h * 3), 2.2, COL.tower);  // start tower
  }
}

// ----- archetypes from the real TLM catalogue (see bake OBJECT_ARCHETYPES)

function utility(A, o) {
  // Wasserversorgung: the small flat-roofed concrete reservoir hut
  A.box(o.x, o.y, o.z, 3.2, o.h, 2.6, COL.utility);
  A.box(o.x, o.y + o.h, o.z, 3.5, 0.18, 2.9, [0.5, 0.49, 0.47]);
}

function waterfall(A, o) {
  // white cascade ribbon, top at the surveyed Z where TLM carries it
  const c = COL.waterfall;
  A.quad([o.x - 1.2, o.y, o.z], [o.x + 1.2, o.y, o.z],
    [o.x + 0.8, o.y + o.h, o.z + 0.4], [o.x - 0.8, o.y + o.h, o.z + 0.4], c);
  A.box(o.x, o.y, o.z + 0.3, 3.0, 0.25, 1.6, c);   // plunge-pool foam
}

function checkdam(A, o) {
  // Torrent check dam (bake _checkdams, rev >= 86): a 1 m concrete wall on
  // the TLM line. Over the ribbon (|d| <= hw, the spillway notch) the crest
  // sits just under the pool level `top` so the water rides over it (sky.js
  // riverProfile); outside it the wings are LEVEL, 0.8 m higher, and end
  // where the bank rises over them (keyed into the slope — a crest that
  // followed the ground read as grey fins climbing the banks). The foot
  // reaches below the plunge level so the DTM's blurred step behind the sheet
  // is hidden. Concrete colour, not the ortho: at 2 m the pixel is foam/forest.
  const f = o.fall;
  if (!f) return wallRuns(A, o, 0.35, 0.35);
  const c = COL.checkdam, th = 0.5;
  for (const run of o.runs || []) {
    if (run.length < 2) continue;
    const e = run[run.length - 1], b0 = run[0];
    const lx = e[0] - b0[0], lz = e[2] - b0[2], ll = Math.hypot(lx, lz) || 1;
    const px = -lz / ll * th, pz = lx / ll * th;
    const top = (p) => {
      const d = Math.abs((p[0] - f.x) * lx / ll + (p[2] - f.z) * lz / ll);
      return d <= f.hw ? f.top - 0.05 : f.top + 0.8;
    };
    const buried = (p) => p[1] > f.top + 0.8;
    const bot = (p) => Math.min(p[1], f.base) - 0.6;
    for (let i = 0; i + 1 < run.length; i++) {
      const p = run[i], q = run[i + 1];
      if (buried(p) && buried(q)) continue;
      const tp = top(p), tq = top(q), bp = bot(p), bq = bot(q);
      A.quad([p[0] + px, bp, p[2] + pz], [q[0] + px, bq, q[2] + pz],
        [q[0] + px, tq, q[2] + pz], [p[0] + px, tp, p[2] + pz], c);
      A.quad([q[0] - px, bq, q[2] - pz], [p[0] - px, bp, p[2] - pz],
        [p[0] - px, tp, p[2] - pz], [q[0] - px, tq, q[2] - pz], c);
      A.quad([p[0] + px, tp, p[2] + pz], [q[0] + px, tq, q[2] + pz],
        [q[0] - px, tq, q[2] - pz], [p[0] - px, tp, p[2] - pz], c);
    }
    for (const [p, sgn] of [[run[0], 1], [e, -1]]) {        // end caps
      if (buried(p)) continue;
      const tp = top(p), bp = bot(p);
      A.quad([p[0] - px * sgn, bp, p[2] - pz * sgn], [p[0] + px * sgn, bp, p[2] + pz * sgn],
        [p[0] + px * sgn, tp, p[2] + pz * sgn], [p[0] - px * sgn, tp, p[2] - pz * sgn], c);
    }
  }
}

function jetty(A, o) {
  // planked deck strip just above the water
  const c = oc(o, 'jetty');
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, y0, z0] = run[i], [x1, y1, z1] = run[i + 1];
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz) || 1;
      const px = -dz / L * 1.1, pz = dx / L * 1.1;
      A.quad([x0 + px, y0 + o.h, z0 + pz], [x1 + px, y1 + o.h, z1 + pz],
        [x1 - px, y1 + o.h, z1 - pz], [x0 - px, y0 + o.h, z0 - pz], c);
      A.box((x0 + x1) / 2, Math.min(y0, y1), (z0 + z1) / 2, 0.25, o.h, 0.25, c);
    }
  }
}

function chute(A, o) {
  // Bobbahn: pale U-channel — bed strip + two low side walls
  const c = COL.chute;
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, y0, z0] = run[i], [x1, y1, z1] = run[i + 1];
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz) || 1;
      const px = -dz / L, pz = dx / L;
      A.quad([x0 + px * 1.5, y0 + 0.15, z0 + pz * 1.5], [x1 + px * 1.5, y1 + 0.15, z1 + pz * 1.5],
        [x1 - px * 1.5, y1 + 0.15, z1 - pz * 1.5], [x0 - px * 1.5, y0 + 0.15, z0 - pz * 1.5], c);
      for (const s of [1.5, -1.5]) {
        A.quad([x0 + px * s, y0, z0 + pz * s], [x1 + px * s, y1, z1 + pz * s],
          [x1 + px * s, y1 + o.h, z1 + pz * s], [x0 + px * s, y0 + o.h, z0 + pz * s], c);
      }
    }
  }
}

function lane(A, o) {
  // Laufbahn: flat red running-track ribbon
  const c = oc(o, 'lane');
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, y0, z0] = run[i], [x1, y1, z1] = run[i + 1];
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz) || 1;
      const px = -dz / L * 2.2, pz = dx / L * 2.2;
      A.quad([x0 + px, y0 + 0.12, z0 + pz], [x1 + px, y1 + 0.12, z1 + pz],
        [x1 - px, y1 + 0.12, z1 - pz], [x0 - px, y0 + 0.12, z0 - pz], c);
    }
  }
}

// per-vertex ortho color (bake rev >= 80 `vc`, 0-255): a long earthwork in
// one averaged pixel reads as a painted slab; per-vertex it blends into the
// ground each wall meets. Falls back to the single ring color.
function vcol(o, i, col) {
  const v = o.vc && o.vc[i];
  return v ? [v[0] / 255, v[1] / 255, v[2] / 255] : col;
}

function ringFan(A, o, y, col, useVc = true) {
  // triangulated polygon top at height y (courts, platform/berm/basin tops).
  // useVc=false keeps a declared fill color (basin standing water) out of
  // the per-vertex ortho override. A walkable top cut open over a stair /
  // ramp well (o._top, topTris) draws its cut triangles, each new vertex in
  // its nearest ring vertex's colour.
  if (o._top) {
    const r = o.ring;
    for (const t of o._top) {
      const base = A.pos.length / 3;
      for (const [x, z] of t) {
        let bi = 0, bd = Infinity;
        for (let i = 0; i < r.length; i++) { const d = (r[i][0] - x) ** 2 + (r[i][1] - z) ** 2; if (d < bd) { bd = d; bi = i; } }
        const cc = A.shade(0, 1, 0, useVc ? vcol(o, bi, col) : col);
        A.pos.push(x, y, z);
        A.col.push(cc[0], cc[1], cc[2]);
      }
      A.idx.push(base, base + 1, base + 2);
    }
    return;
  }
  const pts2 = (o.ring || []).map((p) => new THREE.Vector2(p[0], p[1]));
  if (pts2.length < 3) return;
  const tris = THREE.ShapeUtils.triangulateShape(pts2, []);
  const base = A.pos.length / 3;
  for (let i = 0; i < pts2.length; i++) {
    const cc = A.shade(0, 1, 0, useVc ? vcol(o, i, col) : col);
    A.pos.push(pts2[i].x, y, pts2[i].y);
    A.col.push(cc[0], cc[1], cc[2]);
  }
  for (const t of tris) A.idx.push(base + t[0], base + t[1], base + t[2]);
}

// A ring vertex draped into a CARVED opening — a road underpass through the
// rail corridor, where terrain inside the ring's footprint is excavated to
// the street the way passes under — drags its wall quads down with it: the
// Nänikon-Greifensee platform ring has one vertex at (2604,1880) whose gv is
// the street (443.1) while every neighbour sits on the embankment (~449),
// and the two flanking 35-45 m wall quads swept a 7 m grey curtain across
// the whole underpass ("skirt down to the street"). Ground a structure MEETS
// never spikes metres below the local run of its own footprint; a carve
// under it is an opening, not ground. Window-5 wrapped median despike: a pit
// sample (up to 2 adjacent) snaps to the local median, while real slopes —
// gradual across vertices — pass through unchanged. Same doctrine as
// bake.py's `_despike` (defect rejection), applied at the consumer because
// `gv` is baked into shipped artifacts.
function despikeGv(gv) {
  const n = gv.length;
  if (n < 5) return gv;
  let out = null;
  for (let i = 0; i < n; i++) {
    const w = [];
    for (let k = -2; k <= 2; k++) w.push(gv[(i + k + n) % n]);
    w.sort((a, b) => a - b);
    if (gv[i] < w[2] - 2.5) { (out = out || gv.slice())[i] = w[2]; }
  }
  return out || gv;
}

function ringWalls(A, o, y0, y1, col) {
  // Per-vertex base (bake rev >= 80 `gv`): each wall quad drops to ITS OWN
  // terrain instead of one shared ring-median base — a base picked at the
  // median floated the downhill walls of every ring on a slope (the Celerina
  // schutzdamm hung 2.5 m over the meadow and read as a windowless
  // building). 0.3 m sink hides the terrain seam; a wall whose ground is
  // above the top (uphill side, buried) degenerates to a sliver inside the
  // terrain instead of an inverted quad.
  const r = o.ring || [], gv = o.gv && despikeGv(o.gv);
  for (let i = 0; i + 1 < r.length; i++) {
    const b0 = gv ? Math.min(gv[i] - 0.3, y1 - 0.05) : y0;
    const b1 = gv ? Math.min(gv[i + 1] - 0.3, y1 - 0.05) : y0;
    A.quad([r[i][0], b0, r[i][1]], [r[i + 1][0], b1, r[i + 1][1]],
      [r[i + 1][0], y1, r[i + 1][1]], [r[i][0], y1, r[i][1]], vcol(o, i, col));
  }
}

// ---------------------------------------------------- wells through a top
// (owner: a platform "is not cut open where stairs or ways are coming up from
// the underpass below") A WALKABLE TOP IS OPEN OVER A WELL: a drawn way
// ribbon (ways2.js pieces, the open-to-sky lane: bores and decks are not
// pieces; never a track bed) that DIVES inside the ring — a quad more than WELL_DIVE_M under
// the ring's own ground o.y — is a stair / ramp well; every quad of that
// piece inside the ring and under the top is cut out of the top (widened
// WELL_PAD_M each side, to the trench's cheek walls), drawn and felt alike
// (colEntries reads the same triangles). A way at the ring's ground level
// (a path along a platform, under the slab) cuts nothing: the slab covers it.
const WELL_DIVE_M = 0.5, WELL_PAD_M = 0.1, WELL_TOP_EPS = 0.02;

// keep the part of convex polygon P (CCW [[x, z]]) where f(p) >= 0
function clipHalf(P, f) {
  const out = [];
  for (let i = 0; i < P.length; i++) {
    const a = P[i], b = P[(i + 1) % P.length], fa = f(a), fb = f(b);
    if (fa >= 0) out.push(a);
    if ((fa >= 0) !== (fb >= 0)) { const t = fa / (fa - fb); out.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]); }
  }
  return out;
}
const area2 = (P) => { let s = 0; for (let i = 0; i < P.length; i++) { const a = P[i], b = P[(i + 1) % P.length]; s += a[0] * b[1] - b[0] * a[1]; } return s; };

// convex P minus convex C (both CCW) -> convex pieces
function minusConvex(P, C) {
  const out = [];
  let rest = P;
  for (let k = 0; k < C.length && rest.length >= 3; k++) {
    const a = C[k], b = C[(k + 1) % C.length];
    const side = (p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const outside = clipHalf(rest, (p) => -side(p));
    if (outside.length >= 3 && area2(outside) > 1e-6) out.push(outside);
    rest = clipHalf(rest, side);
  }
  return out;
}

// the convex hull of a few [x, z] points, CCW
function hull(pts) {
  const P = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cr = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const p of P) { while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], p) <= 0) lo.pop(); lo.push(p); }
  for (const p of P.reverse()) { while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], p) <= 0) up.pop(); up.push(p); }
  return lo.slice(0, -1).concat(up.slice(0, -1));
}

// the wells of every walkable ring: sets o._top (the cut top triangles,
// [[x, z] x 3]) on a ring a diving ribbon crosses -> stats
// R8d6: STEPS (buildObjectsSteps): a step per WELL_STEP ribbon quads of the
// grid, a step per walkable ring cut; the same loops in the same order
const WELL_STEP = 2048;
function* cutWellsSteps(objects, pieces) {
  const st = { rings: 0, wells: 0, quads: 0 };
  if (!pieces || !pieces.length) return st;
  // ribbon quads by 16 m cell
  const CELL = 16, grid = new Map(), quads = [];
  for (let k = 0; k < pieces.length; k++) {
    const p = pieces[k];
    if (p.rails) continue;    // a track bed under a platform's edge is no well
    for (let q = 0; q + 1 < p.sec.length; q++) {
      const A = p.sec[q], B = p.sec[q + 1], V = [A.a, A.b, B.b, B.a];
      const x0 = Math.min(...V.map((v) => v[0])), x1 = Math.max(...V.map((v) => v[0]));
      const z0 = Math.min(...V.map((v) => v[2])), z1 = Math.max(...V.map((v) => v[2]));
      const id = quads.length;
      quads.push({ k, V, x0, x1, z0, z1, y0: Math.min(...V.map((v) => v[1])) });
      for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
        for (let cz = Math.floor(z0 / CELL); cz <= Math.floor(z1 / CELL); cz++) {
          const key = cx + '_' + cz;
          if (!grid.has(key)) grid.set(key, []);
          grid.get(key).push(id);
        }
      }
      if (quads.length % WELL_STEP === 0) yield;
    }
  }
  const inRing = (r, x, z) => {
    let c = false;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [xi, zi] = r[i], [xj, zj] = r[j];
      if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
    }
    return c;
  };
  for (const o of objects) {
    if (!WALK_RING.has(o.a) || o.a === 'basin' || !o.ring || o.ring.length < 4) continue;
    yield;                                   // one ring a step
    const r = o.ring, top = o.y + o.h;
    const x0 = Math.min(...r.map((p) => p[0])), x1 = Math.max(...r.map((p) => p[0]));
    const z0 = Math.min(...r.map((p) => p[1])), z1 = Math.max(...r.map((p) => p[1]));
    const seen = new Set(), hit = [];
    for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
      for (let cz = Math.floor(z0 / CELL); cz <= Math.floor(z1 / CELL); cz++) {
        for (const id of grid.get(cx + '_' + cz) || []) {
          if (seen.has(id)) continue;
          seen.add(id);
          const Q = quads[id];
          if (Q.x1 < x0 || Q.x0 > x1 || Q.z1 < z0 || Q.z0 > z1 || Q.y0 >= top - WELL_TOP_EPS) continue;
          if (!Q.V.some((v) => inRing(r, v[0], v[2]))) continue;
          hit.push(Q);
        }
      }
    }
    // a piece is a well only where it dives under the ring's own ground
    const dives = new Set(hit.filter((Q) => Q.y0 < o.y - WELL_DIVE_M).map((Q) => Q.k));
    const cuts = hit.filter((Q) => dives.has(Q.k));
    if (!cuts.length) continue;
    // the top as convex triangles (CCW), minus every well quad (widened across)
    const pts2 = r.map((p) => new THREE.Vector2(p[0], p[1]));
    let tris;
    try { tris = THREE.ShapeUtils.triangulateShape(pts2, []); } catch { continue; }
    let polys = tris.map((t) => { const P = t.map((i) => [pts2[i].x, pts2[i].y]); return area2(P) < 0 ? P.reverse() : P; });
    for (const Q of cuts) {
      const pad = (a, b) => {
        const dx = b[0] - a[0], dz = b[2] - a[2], L = Math.hypot(dx, dz) || 1;
        return [[a[0] - (dx / L) * WELL_PAD_M, a[2] - (dz / L) * WELL_PAD_M], [b[0] + (dx / L) * WELL_PAD_M, b[2] + (dz / L) * WELL_PAD_M]];
      };
      const C = hull([...pad(Q.V[0], Q.V[1]), ...pad(Q.V[3], Q.V[2])]);
      if (C.length < 3) continue;
      const cx0 = Math.min(...C.map((p) => p[0])), cx1 = Math.max(...C.map((p) => p[0]));
      const cz0 = Math.min(...C.map((p) => p[1])), cz1 = Math.max(...C.map((p) => p[1]));
      const next = [];
      for (const P of polys) {
        if (Math.max(...P.map((p) => p[0])) < cx0 || Math.min(...P.map((p) => p[0])) > cx1
          || Math.max(...P.map((p) => p[1])) < cz0 || Math.min(...P.map((p) => p[1])) > cz1) { next.push(P); continue; }
        for (const R of minusConvex(P, C)) next.push(R);
      }
      polys = next;
      st.quads++;
    }
    const out = [];
    for (const P of polys) for (let i = 1; i + 1 < P.length; i++) out.push([P[0], P[i], P[i + 1]]);
    o._top = out;
    st.rings++;
    st.wells += dives.size;
  }
  return st;
}

function court(A, o) {
  ringFan(A, o, o.y + 0.15, oc(o, 'court'));       // flat colored plate
}

// the ring top of a ring dam (BUILDERS.dam; colour key 'platform')
function platform(A, o) {
  const c = oc(o, 'platform');
  ringWalls(A, o, o.y, o.y + o.h, c);
  ringFan(A.tops || A, o, o.y + o.h, c);
}

function basin(A, o) {
  const c = oc(o, 'basin');
  ringWalls(A, o, o.y, o.y + o.h, c);
  ringFan(A, o, o.y + o.h - 0.15, [0.3, 0.42, 0.5], false);   // standing water
}

function berm(A, o) {
  const c = oc(o, 'berm');
  ringWalls(A, o, o.y, o.y + o.h, c);
  ringFan(A.tops || A, o, o.y + o.h, c);
}

function generic(A, o) {
  if (o.runs) { wallRuns(A, o, 0.4, 0.4); return; }
  if (o.ring) {
    const c = oc(o, o.a) || COL.generic;
    for (let i = 0; i + 1 < o.ring.length; i++) {
      const [x0, z0] = o.ring[i], [x1, z1] = o.ring[i + 1];
      A.quad([x0, o.y, z0], [x1, o.y, z1], [x1, o.y + o.h, z1], [x0, o.y + o.h, z0], c);
    }
    return;
  }
  A.box(o.x, o.y, o.z, 2.0, o.h, 2.0, COL.generic);
}

// ---------------------------------------------- DETAIL-1: barriers + pylons
// OSM linear barriers (bake.py osm_barriers_power): every run vertex IS a
// post (the bake densified fences to 2.5 m, guard rails to 2 m), grounded on
// the shipped surface; an opening (gate / crossing way) is a gap between runs
// — nothing drawn, nothing felt. Fences, guard rails and hedges are TIERED
// (BARRIER_TIER): full posts + rails <= near_m of the camera, ONE strip per
// segment in the same colours <= strip_m, nothing beyond — written into a
// preallocated tail of THE objects mesh / cable lines (no new draw, no new
// material or program), rebuilt from the render itself (onBeforeRender, once
// a frame) after the camera moved rebuild_m. The tail is capped (cap_tris /
// cap_lines, nearest segments first) and validate charges the cap.
export const BARRIER_TIER = { near_m: 120, strip_m: 600, rebuild_m: 20, cap_tris: 8000, cap_lines: 2000 };
const TIERED = new Set(['fence', 'guard_rail', 'hedge']);
// the collider strip a solid barrier registers: its drawn width (drawn ==
// felt), never under 0.32 m — game/bldcol.js cleanRing deletes a ring whose
// short edge is < 0.3 m between two antiparallel long ones (its "blunt
// needle"), so a thinner strip would silently not be there
export const BARRIER_COL_W = { fence: 0.32, guard_rail: 0.32, hedge: 0.7, wall: 0.35 };
const MAT_COL = {
  wood: [0.47, 0.36, 0.25], metal: [0.36, 0.37, 0.39], concrete: [0.66, 0.65, 0.62],
  stone: [0.6, 0.58, 0.54], plastic: [0.85, 0.85, 0.83],
};
const GALV = [0.62, 0.64, 0.66];
// fence styles: [index, default material colour, wire count drawn as lines]
const FENCE_ST = {
  rail: 0, knee: 1, board: 2, paling: 3, metal: 4, chain: 5, electric: 6, wire: 7,
};
const ST_COL = [MAT_COL.wood, MAT_COL.wood, MAT_COL.wood, [0.55, 0.45, 0.33], MAT_COL.metal, GALV, MAT_COL.wood, MAT_COL.wood];
const GUARD = 8, HEDGE = 9;
const HEDGE_COL = [0.2, 0.33, 0.15], HEDGE_W = 0.7;
const SEE_THROUGH = (st) => st === 5 || st === 6 || st === 7;

function barrierStyle(o) {
  if (o.a === 'guard_rail') return GUARD;
  if (o.a === 'hedge') return HEDGE;
  const s = FENCE_ST[o.st];
  return s === undefined ? 0 : s;
}
function barrierCol(o, st) {
  if (st === GUARD) return GALV;
  if (st === HEDGE) return HEDGE_COL;
  return (o.m && MAT_COL[o.m]) || ST_COL[st];
}

// a square-section beam between two points (4 sides, 8 tris)
function beam(A, p0, p1, w, col) {
  const d = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
  const L = Math.hypot(d[0], d[1], d[2]) || 1;
  d[0] /= L; d[1] /= L; d[2] /= L;
  // u ⟂ d (horizontal when d is not vertical-ish), v = d x u
  let u = Math.abs(d[1]) < 0.95 ? [-d[2], 0, d[0]] : [1, 0, 0];
  const lu = Math.hypot(u[0], u[1], u[2]) || 1;
  u = [u[0] / lu * w / 2, u[1] / lu * w / 2, u[2] / lu * w / 2];
  const v = [d[1] * u[2] - d[2] * u[1], d[2] * u[0] - d[0] * u[2], d[0] * u[1] - d[1] * u[0]];
  const c = (p, a, b) => [p[0] + u[0] * a + v[0] * b, p[1] + u[1] * a + v[1] * b, p[2] + u[2] * a + v[2] * b];
  const K = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  for (let i = 0; i < 4; i++) {
    const [a0, b0] = K[i], [a1, b1] = K[(i + 1) % 4];
    A.quad(c(p0, a0, b0), c(p0, a1, b1), c(p1, a1, b1), c(p1, a0, b0), col);
  }
}

// NEAR tier: one segment run[i] -> run[i+1] of a barrier; post at the start
// vertex (endPost: and at the end — a run's last vertex). L: line sink
// (x0, y0, z0, x1, y1, z1, d) for wires, d the kind's wire diameter (wire.js fenceWireD).
function barrierNear(A, L, st, col, h, p, q, endPost) {
  const [x0, y0, z0] = p, [x1, y1, z1] = q;
  const dx = x1 - x0, dz = z1 - z0, len = Math.hypot(dx, dz);
  const yaw = Math.atan2(dz, dx);
  const at = (f, y) => [x0 + dx * f, (y0 + (y1 - y0) * f) + y, z0 + dz * f];
  const band = (ya, yb, c, off = 0) => {
    const ox = -dz / len * off, oz = dx / len * off;
    A.quad([x0 + ox, y0 + ya, z0 + oz], [x1 + ox, y1 + ya, z1 + oz], [x1 + ox, y1 + yb, z1 + oz], [x0 + ox, y0 + yb, z0 + oz], c);
  };
  if (st === HEDGE) {
    const px = -dz / len * HEDGE_W / 2, pz = dx / len * HEDGE_W / 2;
    A.quad([x0 + px, y0, z0 + pz], [x1 + px, y1, z1 + pz], [x1 + px, y1 + h, z1 + pz], [x0 + px, y0 + h, z0 + pz], col);
    A.quad([x1 - px, y1, z1 - pz], [x0 - px, y0, z0 - pz], [x0 - px, y0 + h, z0 - pz], [x1 - px, y1 + h, z1 - pz], col);
    A.quad([x0 + px, y0 + h, z0 + pz], [x1 + px, y1 + h, z1 + pz], [x1 - px, y1 + h, z1 - pz], [x0 - px, y0 + h, z0 - pz], col);
    return;
  }
  const thin = st === 6 || st === 7;
  const pw = st === GUARD ? 0.12 : thin ? 0.06 : st === 4 || st === 5 ? 0.07 : 0.11;
  const pc = st === GUARD ? MAT_COL.metal : col;
  const post = (x, y, z) => A.box(x, y, z, pw, h, pw, pc, yaw);
  post(x0, y0, z0);
  if (endPost) post(x1, y1, z1);
  if (st === GUARD) {
    // W-beam: a two-faceted band 0.31 m deep centred at 0.6 h on the post face
    const off = 0.12;
    const ox = -dz / len * off, oz = dx / len * off, bx = -dz / len * 0.05, bz = dx / len * 0.05;
    const yc = h * 0.8, hb = 0.155;
    A.quad([x0 + ox, y0 + yc - hb, z0 + oz], [x1 + ox, y1 + yc - hb, z1 + oz], [x1 + ox + bx, y1 + yc, z1 + oz + bz], [x0 + ox + bx, y0 + yc, z0 + oz + bz], col);
    A.quad([x0 + ox + bx, y0 + yc, z0 + oz + bz], [x1 + ox + bx, y1 + yc, z1 + oz + bz], [x1 + ox, y1 + yc + hb, z1 + oz], [x0 + ox, y0 + yc + hb, z0 + oz], col);
    return;
  }
  switch (st) {
    case 0: band(h * 0.45 - 0.05, h * 0.45 + 0.05, col); band(h * 0.9 - 0.05, h * 0.9 + 0.05, col); break;   // 2-rail
    case 1: band(h * 0.85 - 0.06, h * 0.85 + 0.06, col); break;                                          // knee rail
    case 2: band(0.06, h * 0.98, col); break;                                                             // boards
    case 3: band(0.12, h * 0.92, col); band(h * 0.75 - 0.04, h * 0.75 + 0.04, col, 0.03); break;        // paling
    case 4: {                                                                                             // metal bars
      band(h - 0.06, h, col); band(0.1, 0.16, col);
      const n = Math.max(1, Math.round(len / 0.6));
      for (let k = 1; k < n; k++) {
        const a = at(k / n, 0.1), b = at(k / n, h);
        A.quad([a[0] - Math.cos(yaw) * 0.015, a[1], a[2] - Math.sin(yaw) * 0.015], [a[0] + Math.cos(yaw) * 0.015, a[1], a[2] + Math.sin(yaw) * 0.015],
          [b[0] + Math.cos(yaw) * 0.015, b[1], b[2] + Math.sin(yaw) * 0.015], [b[0] - Math.cos(yaw) * 0.015, b[1], b[2] - Math.sin(yaw) * 0.015], col);
      }
      break;
    }
    case 5: {                                                                                             // chain-link: top rail + a see-through mesh
      band(h - 0.05, h, col);
      const d = fenceWireD(st, h);
      for (const f of [0.08, 0.3, 0.52, 0.74]) { const a = at(0, h * f), b = at(1, h * f); L(a[0], a[1], a[2], b[0], b[1], b[2], d); }
      const a = at(0, h * 0.08), b = at(0.5, h * 0.97), c = at(1, h * 0.08);
      L(a[0], a[1], a[2], b[0], b[1], b[2], d); L(b[0], b[1], b[2], c[0], c[1], c[2], d);
      break;
    }
    default: {                                                                                            // electric / wire: wires only
      const d = fenceWireD(st, h);
      for (const f of st === 6 ? [0.45, 0.8] : [0.35, 0.65, 0.95]) { const a = at(0, h * f), b = at(1, h * f); L(a[0], a[1], a[2], b[0], b[1], b[2], d); }
    }
  }
}

// STRIP tier: one band per segment in the barrier's colours (a see-through
// fence: its top rail / wire band only, never a wall)
function barrierStrip(A, st, col, h, p, q) {
  const [x0, y0, z0] = p, [x1, y1, z1] = q;
  const ya = SEE_THROUGH(st) ? h * 0.85 : st === 0 ? h * 0.4 : st === GUARD ? h * 0.65 : 0.05;
  A.quad([x0, y0 + ya, z0], [x1, y1 + ya, z1], [x1, y1 + h, z1], [x0, y0 + h, z0], col);
}

// tower / pole arms: level k (0 = top) -> {y above ground, half span}; the
// pylon builder and the cable attach points share it
export function pylonArms(h, w, kc, lv) {
  const out = [];
  const n = Math.max(1, lv | 0);
  if (kc === 1) {                                      // pole: crossarms 1.2 m apart
    for (let k = 0; k < n; k++) out.push({ y: h - 0.4 - k * 1.2, half: 1.1 });
    return out;
  }
  const gap = Math.max(2.5, h * 0.12);
  for (let k = 0; k < n; k++) out.push({ y: h - 2.2 - k * gap, half: Math.max(2.5, w * 0.55 + h * 0.07) * (k === 1 ? 1.15 : 1) });
  return out;
}
// cable c (0 .. 3 ci - 1) of a line on a support -> [x, y, z]
export function attach(x, y, z, h, yaw, w, kc, lv, c) {
  const arms = pylonArms(h, w, kc, lv);
  const a = arms[Math.min(arms.length - 1, Math.floor(c / 3))];
  const ph = (c % 3) - 1;                              // -1, 0, +1 along the arm
  const off = ph * a.half * 0.85, nx = -Math.sin(yaw), nz = Math.cos(yaw);
  return [x + nx * off, y + a.y - (kc === 1 ? 0 : 0.9), z + nz * off];
}

function pylon(A, o) {
  const kc = o.k === 'pole' ? 1 : o.k === 'portal' ? 2 : o.k === 'terminal' ? 3 : 0;
  const { x, y, z, h, yaw } = o;
  const arms = pylonArms(h, o.w, kc, o.lv);
  const ax = -Math.sin(yaw), az = Math.cos(yaw), lx = Math.cos(yaw), lz = Math.sin(yaw);
  if (kc === 1) {
    const c = [0.5, 0.45, 0.4];
    A.box(x, y, z, 0.3, h, 0.3, c, yaw);
    for (const a of arms) A.box(x, y + a.y - 0.08, z, 0.12, 0.12, a.half * 2 + 0.2, c, yaw);
    return;
  }
  const c = COL.powerline;
  if (kc === 2) {                                      // portal: two legs + a beam
    const a = arms[0], s = a.half;
    for (const sg of [-1, 1]) A.box(x + ax * s * sg, y, z + az * s * sg, 0.5, h, 0.5, c, yaw);
    A.box(x, y + a.y - 0.25, z, 0.5, 0.5, s * 2 + 0.6, c, yaw);
    return;
  }
  // lattice tower (tower / terminal): 4 legs in two leaning sections, flat
  // X-braces on the lower faces, one crossarm per level, a peak
  const b = o.w / 2, nk = h * 0.62, bn = b * 0.32, bt = b * 0.22;
  const corner = (r, yy, sx, sz) => [x + (lx * sx + ax * sz) * r, y + yy, z + (lz * sx + az * sz) * r];
  const C = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  for (const [sx, sz] of C) {
    beam(A, corner(b, 0, sx, sz), corner(bn, nk, sx, sz), 0.28, c);
    beam(A, corner(bn, nk, sx, sz), corner(bt, h, sx, sz), 0.22, c);
  }
  for (let i = 0; i < 4; i++) {
    const [sa, ta] = C[i], [sb, tb] = C[(i + 1) % 4];
    const p0 = corner(b, 0, sa, ta), p1 = corner(bn, nk, sb, tb), q0 = corner(b, 0, sb, tb), q1 = corner(bn, nk, sa, ta);
    for (const [u, v] of [[p0, p1], [q0, q1]]) {
      A.quad([u[0], u[1], u[2]], [u[0], u[1] + 0.25, u[2]], [v[0], v[1] + 0.25, v[2]], [v[0], v[1], v[2]], c);
    }
  }
  for (const a of arms) A.box(x, y + a.y - 0.5, z, 0.6, 0.5, a.half * 2 + 0.4, c, yaw);
  beam(A, [x, y + h, z], [x, y + h + Math.max(1.5, h * 0.06), z], 0.18, c);
}

// OSM power line: cables only (its supports are pylon records), 3 x circuits
// conductors per span through the shared attach() points; the sag as the
// TLM powerline's
function osmPowerline(cables, o) {
  const nc = 3 * Math.max(1, Math.min(3, o.ci | 0));
  (o.runs || []).forEach((run, r) => {
    const S = o.sup[r];
    for (let i = 0; i + 1 < run.length; i++) {
      const [xa, ya, za] = run[i], [xb, yb, zb] = run[i + 1];
      const sa = S[i], sb = S[i + 1];
      const span = Math.hypot(xb - xa, zb - za);
      const sag = Math.min(5, span * 0.045);
      const N = 8, d = wireDiameter(o.kv, sa[3]);   // the span's conductor: its line's kV, a pole span = distribution
      for (let c = 0; c < nc; c++) {
        const p = attach(xa, ya, za, sa[0], sa[1], sa[2], sa[3], sa[4], c);
        const q = attach(xb, yb, zb, sb[0], sb[1], sb[2], sb[3], sb[4], c);
        const cy = (t) => p[1] + (q[1] - p[1]) * t - sag * 4 * t * (1 - t);
        for (let k = 0; k < N; k++) {
          const t0 = k / N, t1 = (k + 1) / N;
          cables.seg(p[0] + (q[0] - p[0]) * t0, cy(t0), p[2] + (q[2] - p[2]) * t0,
            p[0] + (q[0] - p[0]) * t1, cy(t1), p[2] + (q[2] - p[2]) * t1, d);
        }
      }
    }
  });
}

// the tiered tail: a typed-array Acc (quads only) + a line sink, both capped
class TailAcc extends Acc {
  constructor(pos, col, capQuads) { super(); this.P = pos; this.Cc = col; this.cap = capQuads; this.n = 0; this.full = false; }
  quad(a, b, c2, d, col) {
    if (this.n >= this.cap) { this.full = true; return; }
    const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], v = [d[0] - a[0], d[1] - a[1], d[2] - a[2]];
    const nx = u[1] * v[2] - u[2] * v[1], ny = u[2] * v[0] - u[0] * v[2], nz = u[0] * v[1] - u[1] * v[0];
    const l = Math.hypot(nx, ny, nz) || 1;
    const cc = this.shade(nx / l, ny / l, nz / l, col);
    let o = this.n * 12;
    for (const p of [a, b, c2, d]) {
      this.P[o] = p[0]; this.P[o + 1] = p[1]; this.P[o + 2] = p[2];
      this.Cc[o] = cc[0]; this.Cc[o + 1] = cc[1]; this.Cc[o + 2] = cc[2];
      o += 3;
    }
    this.n++;
  }
}

// the tiered barrier segments, flat: per segment x0 y0 z0 x1 y1 z1 h st endPost
// + its colour; a 120 m grid of segment ids
export class BarrierTail {
  constructor(objects) {
    const S = [], C = [];
    for (const o of objects) {
      if (!TIERED.has(o.a)) continue;
      const st = barrierStyle(o), col = barrierCol(o, st);
      for (const run of o.runs || []) {
        for (let i = 0; i + 1 < run.length; i++) {
          const p = run[i], q = run[i + 1];
          if (Math.hypot(q[0] - p[0], q[2] - p[2]) < BARRIER_MIN_L) continue;
          S.push(p[0], p[1], p[2], q[0], q[1], q[2], o.h, st, i + 2 === run.length ? 1 : 0);
          C.push(col);
        }
      }
    }
    this.seg = Float64Array.from(S); this.col = C; this.n = C.length;
    this.cell = BARRIER_TIER.near_m;
    this.grid = new Map();
    for (let s = 0; s < this.n; s++) {
      const b = s * 9, k = `${Math.floor((this.seg[b] + this.seg[b + 3]) / 2 / this.cell)},${Math.floor((this.seg[b + 2] + this.seg[b + 5]) / 2 / this.cell)}`;
      if (!this.grid.has(k)) this.grid.set(k, []);
      this.grid.get(k).push(s);
    }
    this.last = null;
  }

  // the segments within strip_m of (cx, cz), nearest first -> [[d2, s]]
  visible(cx, cz) {
    const R = BARRIER_TIER.strip_m, r = Math.ceil(R / this.cell) + 1, R2 = R * R;
    const gx = Math.floor(cx / this.cell), gz = Math.floor(cz / this.cell), out = [];
    for (let i = -r; i <= r; i++) {
      for (let j = -r; j <= r; j++) {
        const ids = this.grid.get(`${gx + i},${gz + j}`);
        if (!ids) continue;
        for (const s of ids) {
          const b = s * 9, mx = (this.seg[b] + this.seg[b + 3]) / 2 - cx, mz = (this.seg[b + 2] + this.seg[b + 5]) / 2 - cz;
          const d2 = mx * mx + mz * mz;
          if (d2 <= R2) out.push([d2, s]);
        }
      }
    }
    out.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    return out;
  }

  // write the tiers around (cx, cz) into pos / col (quads) and lines (xyz
  // pairs; wires, when given: per vertex the unit tangent + the diameter)
  // -> {quads, lines, near, strip, cut}
  write(cx, cz, pos, col, capQuads, lines, capLines, wires = null) {
    const T = new TailAcc(pos, col, capQuads);
    let nl = 0, near = 0, strip = 0, cut = 0, lfull = false;
    const L = (x0, y0, z0, x1, y1, z1, d) => {
      if (nl >= capLines) { lfull = true; return; }
      const o = nl * 6;
      lines[o] = x0; lines[o + 1] = y0; lines[o + 2] = z0; lines[o + 3] = x1; lines[o + 4] = y1; lines[o + 5] = z1;
      if (wires) {
        const l = Math.hypot(x1 - x0, y1 - y0, z1 - z0) || 1, w = nl * 8;
        wires[w] = wires[w + 4] = (x1 - x0) / l; wires[w + 1] = wires[w + 5] = (y1 - y0) / l;
        wires[w + 2] = wires[w + 6] = (z1 - z0) / l; wires[w + 3] = wires[w + 7] = d;
      }
      nl++;
    };
    const N2 = BARRIER_TIER.near_m * BARRIER_TIER.near_m, sg = this.seg;
    const vis = this.visible(cx, cz);
    for (let k = 0; k < vis.length; k++) {
      const [d2, s] = vis[k];
      const b = s * 9, p = [sg[b], sg[b + 1], sg[b + 2]], q = [sg[b + 3], sg[b + 4], sg[b + 5]];
      const h = sg[b + 6], st = sg[b + 7], n0 = T.n, l0 = nl;
      const isNear = d2 <= N2;
      if (isNear) barrierNear(T, L, st, this.col[s], h, p, q, sg[b + 8] === 1); else barrierStrip(T, st, this.col[s], h, p, q);
      if (T.full || lfull) {
        // the cap: a segment is whole or absent, the farther ones are cut
        T.n = n0; nl = l0; cut = vis.length - k;
        break;
      }
      if (isNear) near++; else strip++;
    }
    return { quads: T.n, lines: nl, near, strip, cut };
  }
}

const BUILDERS = {
  mast: (A, cb, o) => mast(A, o),
  tower: (A, cb, o) => tower(A, o),
  chimney: (A, cb, o) => chimney(A, o),
  windturbine: (A, cb, o) => windturbine(A, o),
  silo: (A, cb, o) => silo(A, o),
  wall: (A, cb, o) => wallRuns(A, o, 0.35, 0.35),
  dam: (A, cb, o) => (o.ring ? platform(A, o) : wallRuns(A, o, 3.0, 3.0)),
  avalanche: (A, cb, o) => avalanche(A, o),
  powerline: (A, cb, o) => (o.sup ? osmPowerline(cb, o) : powerline(A, cb, o)),
  pylon: (A, cb, o) => pylon(A, o),
  coping: (A, cb, o) => wallRuns(A, o, 0.3, 0.3),
  // tiered (BarrierTail): drawn by the tail, never statically
  fence: () => {}, guard_rail: () => {}, hedge: () => {},
  fountain: (A, cb, o) => fountain(A, o),
  monument: (A, cb, o) => monument(A, o),
  skijump: (A, cb, o) => skijump(A, o),
  utility: (A, cb, o) => utility(A, o),
  waterfall: (A, cb, o) => waterfall(A, o),
  checkdam: (A, cb, o) => checkdam(A, o),
  jetty: (A, cb, o) => jetty(A, o),
  chute: (A, cb, o) => chute(A, o),
  lane: (A, cb, o) => lane(A, o),
  court: (A, cb, o) => court(A, o),
  basin: (A, cb, o) => basin(A, o),
  berm: (A, cb, o) => berm(A, o),
  generic: (A, cb, o) => generic(A, o),
};

// ------------------------------------------------------------- collision
// Collider entries for the structures a body can plausibly stand on. Data
// shape = the building collider's (APP_BAKER.md §5):
//  - low structures (h <= 1.2 m): EMPTY fp = stand-only surface — the top
//    tris ground a body within the step-up window and are a ceiling from
//    below (roofSpanAt); never a push, so walking past one can't trap.
//  - taller rings (ring dams, berms): fp = the drawn ring
//    -> solid sides (push-out, grabWall-climbable), top tris landable.
// Tris/rings are exactly what the builders above draw — a collider must never
// claim more than the eye can see. Bullets still pass through (server
// occlusion only prisms bake col_* entries — documented in APP_OBJECTS.md).
// (a railway platform is no object: it is paved ground, game/pave.js
// platforms — the pave cut draws it, the heightfields stand on its plane)
const WALK_RING = new Set(['berm', 'basin', 'dam']);

// DETAIL-1: a SOLID barrier (o.solid: OSM fence / guard rail / hedge / wall,
// TLM Mauer / Trockenmauer / Scheibenstand) -> one strip per drawn segment:
// the segment's own xz quad BARRIER_COL_W wide, y0 = its lower end's ground
// - 0.2, y1 = its higher end's top (a push-out wall; on top = standable like
// any building edge). The SAME segments the builders draw (L < BARRIER_MIN_L
// skipped by both) — a gap in the run is a gap in the strips.
export const BARRIER_MIN_L = 0.05;
export function barrierStrips(o, out) {
  const w = (BARRIER_COL_W[o.a] || 0.32) / 2;
  for (const run of o.runs || []) {
    for (let i = 0; i + 1 < run.length; i++) {
      const [x0, ya, z0] = run[i], [x1, yb, z1] = run[i + 1];
      const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz);
      if (L < (o.a === 'wall' ? 0.2 : BARRIER_MIN_L)) continue;     // wallRuns skips < 0.2
      const px = -dz / L * w, pz = dx / L * w;
      // a segment shorter than the strip is wide would be a cleanRing needle
      // too: stretched about its middle to the width (<= 0.16 m each way)
      const e = L < 2 * w ? (2 * w - L) / 2 / L : 0, ex = dx * e, ez = dz * e;
      out.push({
        fp: [[[x0 - ex + px, z0 - ez + pz], [x1 + ex + px, z1 + ez + pz], [x1 + ex - px, z1 + ez - pz], [x0 - ex - px, z0 - ez - pz]]],
        y0: Math.min(ya, yb) - 0.2, y1: Math.max(ya, yb) + o.h,
      });
    }
  }
}

export function colEntries(o, out) {
  if (o.solid) { barrierStrips(o, out); return; }
  if (o.a === 'jetty') {
    const roof = [];
    let y0 = Infinity, y1 = -Infinity;
    for (const run of o.runs || []) {
      for (let i = 0; i + 1 < run.length; i++) {
        const [x0, ya, z0] = run[i], [x1, yb, z1] = run[i + 1];
        const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz);
        if (L < 0.2) continue;
        const px = -dz / L * 1.1, pz = dx / L * 1.1;   // same strip jetty() draws
        const a = [x0 + px, ya + o.h, z0 + pz], b = [x1 + px, yb + o.h, z1 + pz];
        const c = [x1 - px, yb + o.h, z1 - pz], d = [x0 - px, ya + o.h, z0 - pz];
        roof.push([a, b, c], [a, c, d]);
        y0 = Math.min(y0, ya, yb); y1 = Math.max(y1, ya + o.h, yb + o.h);
      }
    }
    if (roof.length) out.push({ fp: [], y0, y1, roof });
    return;
  }
  if (!WALK_RING.has(o.a) || !o.ring || o.ring.length < 3) return;
  const topY = o.y + o.h;
  // same despiked ground the drawn walls use (drawn == felt): a pit sample
  // under a carved opening must not stretch the collider volume down into it
  const gvd = o.gv && despikeGv(o.gv);
  const baseY = gvd ? Math.min(o.y, ...gvd) : o.y;     // walls drop to local ground
  let roof;
  if (o._top) {
    // the top cut open over its wells (cutWellsSteps): the same triangles ringFan draws
    roof = o._top.map((t) => t.map(([x, z]) => [x, topY, z]));
  } else {
    const pts2 = o.ring.map((p) => new THREE.Vector2(p[0], p[1]));
    let tris;
    try { tris = THREE.ShapeUtils.triangulateShape(pts2, []); } catch { return; }
    if (!tris || !tris.length) return;
    roof = tris.map((t) => t.map((i) => [pts2[i].x, topY, pts2[i].y]));
  }
  if (!roof.length) return;
  out.push({
    fp: o.h > 1.2 ? [o.ring.map((p) => [p[0], p[1]])] : [],
    y0: baseY, y1: topY, roof,
  });
}

// ------------------------------------------------------------- entry point

// ONE fetch of objects.json per boot, shared: buildObjects draws it and
// sky.js buildRivers splices its check-dam falls into the ribbons.
let _objP = null;
export function loadObjects(meta, baseUrl) {
  if (!meta.objects || !meta.objects.file) return Promise.resolve(null);
  if (!_objP) {
    _objP = fetchAsset(`${baseUrl}/${meta.objects.file}`, { prio: 'bulk', as: 'json' })
      .catch(() => null);
  }
  return _objP;
}

// piecesP (optional): the drawn way ribbons (ways2.js WaysNet.pieces), the
// wells cutWellsSteps opens in the walkable tops; null / a failure -> no cuts.
// R8d6: the CPU build is a STEP GENERATOR (buildObjectsSteps) run by
// decode.js runSteps under ONE label, 'objects.build' (the live build was
// one 117 ms task after the Beam on a desktop: quad / box / the loop)
export async function buildObjects(scene, meta, baseUrl, collider, piecesP = null) {
  const [data, pieces] = await Promise.all([loadObjects(meta, baseUrl), piecesP ? piecesP.catch(() => null) : null]);
  if (!data) return null;
  return runSteps(buildObjectsSteps(scene, data, pieces, collider, meta), 'objects.build');
}

// the build of a loaded objects.json in steps -> buildObjects' value. A step
// is ONE object (its builder + its collider entries: the live bake's 1943
// records shared ~60 ms of that task, ~30 us each on average), a WELL_STEP
// piece-quad chunk / one ring cut (cutWellsSteps), the barrier tail, the collider tile, and the merged meshes' assembly a step per
// array fill / the index / the bounds / the tops mesh / the cable lines.
// The same code in the same order as the pre-R8d6 one-shot: a drain is it,
// byte for byte (check-objects 'R8d6 steps' pins it against git 17bea48).
// meta: the world's (wire.js wireMaterial reads its light; required once there are cable lines)
export function* buildObjectsSteps(scene, data, pieces, collider, meta = null) {
  const wells = yield* cutWellsSteps(data.objects || [], pieces);
  const A = new Acc();
  // the walkable tops (platform / berm / dam): their own mesh drawn with a
  // depth offset past the lane ribbons' (-2 / -2, roads3d.js) — a ribbon
  // under a slab top (a path along the platform at its ground) no longer
  // pulls through it at grazing angles
  A.tops = new Acc();
  const cables = new Cables();
  const colOut = [];
  for (const o of data.objects || []) {
    // point objects without runs/ring get x/z; line/poly objects carry theirs
    (BUILDERS[o.a] || BUILDERS.generic)(A, cables, o);
    if (collider) colEntries(o, colOut);
    yield;                                   // one object a step
  }
  const tail = new BarrierTail(data.objects || []);
  yield;
  // one synthetic collider tile, grid-indexed, never evicted (the building
  // manager only removes the "tx_ty" keys it loaded itself)
  if (collider && colOut.length) collider.addTile('tlm:objects', { buildings: colOut });
  yield;
  const group = new THREE.Group();
  group.name = 'tlm-objects';
  // the tiered barrier tail rides in THIS mesh / the cable lines: static
  // vertices first, then cap_tris / 2 quads (fixed quad index pattern) and
  // cap_lines line segments whose contents onBeforeRender rewrites
  const capQ = tail.n ? BARRIER_TIER.cap_tris / 2 : 0, capL = tail.n ? BARRIER_TIER.cap_lines : 0;
  const nStatV = A.pos.length / 3, nStatI = A.idx.length, nStatL = cables.pos.length / 3;
  let mesh = null, lines = null;
  if (A.idx.length || capQ) {
    const g = new THREE.BufferGeometry();
    const P = new Float32Array((nStatV + capQ * 4) * 3), C = new Float32Array((nStatV + capQ * 4) * 3);
    P.set(A.pos); C.set(A.col);
    yield;
    const I = new Uint32Array(nStatI + capQ * 6);
    I.set(A.idx);
    for (let q = 0; q < capQ; q++) {
      const b = nStatV + q * 4, o = nStatI + q * 6;
      I[o] = b; I[o + 1] = b + 1; I[o + 2] = b + 2; I[o + 3] = b; I[o + 4] = b + 2; I[o + 5] = b + 3;
    }
    yield;
    const pa = new THREE.BufferAttribute(P, 3), ca = new THREE.BufferAttribute(C, 3);
    if (capQ) { pa.setUsage(THREE.DynamicDrawUsage); ca.setUsage(THREE.DynamicDrawUsage); }
    g.setAttribute('position', pa);
    g.setAttribute('color', ca);
    g.setIndex(new THREE.BufferAttribute(I, 1));
    g.setDrawRange(0, nStatI);
    // the bounds: the static part + every tiered segment at its full height
    // (the zero-filled tail must not pull the sphere to the origin)
    const bb = new THREE.Box3(), v = new THREE.Vector3();
    for (let i = 0; i < nStatV; i++) bb.expandByPoint(v.set(P[i * 3], P[i * 3 + 1], P[i * 3 + 2]));
    for (let s = 0; s < tail.n; s++) {
      const b = s * 9;
      bb.expandByPoint(v.set(tail.seg[b], tail.seg[b + 1], tail.seg[b + 2]));
      bb.expandByPoint(v.set(tail.seg[b + 3], tail.seg[b + 4] + tail.seg[b + 6], tail.seg[b + 5]));
    }
    g.boundingBox = bb;
    g.boundingSphere = bb.getBoundingSphere(new THREE.Sphere());
    mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      vertexColors: true, side: THREE.DoubleSide, fog: true,
    }));
    mesh.matrixAutoUpdate = false;
    group.add(mesh);
    yield;
  }
  if (A.tops.idx.length) {
    const T = A.tops, g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(T.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(T.col, 3));
    g.setIndex(T.idx);
    const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      vertexColors: true, side: THREE.DoubleSide, fog: true,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
    }));
    mesh.matrixAutoUpdate = false;
    group.add(mesh);
    yield;
  }
  if (cables.pos.length || capL) {
    const g = new THREE.BufferGeometry();
    const L = new Float32Array(cables.pos.length + capL * 6), Wr = new Float32Array(cables.wire.length + capL * 8);
    L.set(cables.pos); Wr.set(cables.wire);
    const la = new THREE.BufferAttribute(L, 3), wa = new THREE.BufferAttribute(Wr, 4);
    if (capL) { la.setUsage(THREE.DynamicDrawUsage); wa.setUsage(THREE.DynamicDrawUsage); }
    g.setAttribute('position', la);
    g.setAttribute('aWire', wa);
    g.setDrawRange(0, nStatL);
    if (mesh) { g.boundingBox = mesh.geometry.boundingBox.clone(); g.boundingSphere = mesh.geometry.boundingSphere.clone(); }
    if (cables.pos.length) {
      const bb = g.boundingBox || new THREE.Box3(), v = new THREE.Vector3();
      for (let i = 0; i < nStatL; i++) bb.expandByPoint(v.set(L[i * 3], L[i * 3 + 1], L[i * 3 + 2]));
      g.boundingBox = bb; g.boundingSphere = bb.getBoundingSphere(new THREE.Sphere());
    }
    // wires of their real diameter (wire.js): coverage alpha per eye, the
    // bake's light, a transparent after the water
    lines = new THREE.LineSegments(g, wireMaterial(meta));
    lines.onBeforeRender = wireBeforeRender;
    lines.renderOrder = WIRE_ORDER;
    lines.matrixAutoUpdate = false;
    group.add(lines);
    yield;
  }
  // the tail's per-frame update, from the render itself (VR + flat + phone
  // alike; no main.js hook — trailmarks.js signposts' pattern): a rebuild
  // writes the arrays and marks them; its draw ranges apply on the NEXT
  // render, after three uploaded the new contents (the index is fixed)
  const tstate = { frame: -1, at: null, pending: null, drawn: null };
  const rebuild = (cx, cz) => {
    const mg = mesh.geometry, P = mg.attributes.position.array, C = mg.attributes.color.array;
    const LP = lines ? lines.geometry.attributes.position.array : new Float32Array(0);
    const LW = lines ? lines.geometry.attributes.aWire.array : null;
    const r = tail.write(cx, cz, P.subarray(nStatV * 3), C.subarray(nStatV * 3), capQ,
      LP.subarray(nStatL * 3), lines ? capL : 0, LW && LW.subarray(nStatL * 4));
    // the previous build's farther quads: zeroed, never stale (degenerate)
    const prev = tstate.drawn ? tstate.drawn.quads : 0;
    if (prev > r.quads) P.fill(0, (nStatV + r.quads * 4) * 3, (nStatV + prev * 4) * 3);
    const hi = Math.max(prev, r.quads) * 12;
    for (const a of [mg.attributes.position, mg.attributes.color]) {
      a.clearUpdateRanges(); a.addUpdateRange(nStatV * 3, hi); a.needsUpdate = true;
    }
    if (lines) {
      const la = lines.geometry.attributes.position, lprev = tstate.drawn ? tstate.drawn.lines : 0;
      if (lprev > r.lines) LP.fill(0, (nStatL + r.lines * 2) * 3, (nStatL + lprev * 2) * 3);
      la.clearUpdateRanges(); la.addUpdateRange(nStatL * 3, Math.max(lprev, r.lines) * 6); la.needsUpdate = true;
      const wa = lines.geometry.attributes.aWire;
      wa.clearUpdateRanges(); wa.addUpdateRange(nStatL * 4, r.lines * 8); wa.needsUpdate = true;
    }
    tstate.at = [cx, cz]; tstate.pending = r; tstate.drawn = r;
    return r;
  };
  if (tail.n && mesh) {
    mesh.onBeforeRender = (renderer, _scene, camera) => {
      const f = renderer.info.render.frame;
      if (f === tstate.frame) return;
      tstate.frame = f;
      if (tstate.pending) {
        mesh.geometry.setDrawRange(0, nStatI + tstate.pending.quads * 6);
        if (lines) lines.geometry.setDrawRange(0, nStatL + tstate.pending.lines * 2);
        tstate.pending = null;
      }
      const e = camera.matrixWorld.elements;
      const R = BARRIER_TIER.rebuild_m;
      if (!tstate.at || Math.hypot(e[12] - tstate.at[0], e[14] - tstate.at[1]) > R) rebuild(e[12], e[14]);
    };
  }
  scene.add(group);
  return {
    group, count: (data.objects || []).length, tris: (A.idx.length + A.tops.idx.length) / 3, wells,
    // the tiered tail (check-objects drives it without a renderer)
    tail: { segments: tail.n, cap_tris: capQ * 2, cap_lines: capL, rebuild, state: tstate, mesh, lines, nStatI, nStatL },
  };
}
